import { Bar, CartesianGrid, ComposedChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Card } from "@/components/ui/Card"
import { formatMoney, formatMoneyFull, formatMoneyShort } from "@/lib/money"
import Figure from "./Figure"
import type { MonthPoint } from "./landingData"

interface Props {
  currency: string
  target: number
  actual: number
  estimate: number
  months: MonthPoint[]
}

const ESTIMATE_FILL = "color-mix(in srgb, var(--chart-1) 35%, transparent)"

// Full plan year: target, actual so far, where the year lands, and the months
// behind it. Follows Scope and Category; the Period picker does not apply.
export default function YearLanding({ currency, target, actual, estimate, months }: Props) {
  const gap = estimate - target
  const pct = target > 0 ? Math.round((estimate / target) * 100) : null
  return (
    <Card className="overflow-hidden">
      <div className="border-b px-4 py-3">
        <div className="text-sm font-semibold">Full year 2026 Progress</div>
      </div>
      <div className="grid grid-cols-4 divide-x border-b">
        <Figure label="Year target" value={formatMoneyShort(target, currency)} />
        <Figure label="Actual so far" value={formatMoneyShort(actual, currency)}>
          {target > 0 && `${Math.round((actual / target) * 100)}% of target`}
        </Figure>
        <Figure label="Latest estimate" value={formatMoneyShort(estimate, currency)}>
          {pct != null && `${pct}% of target`}
        </Figure>
        <Figure
          label={gap < 0 ? "Short of target" : "Over target"}
          value={`${gap < 0 ? "−" : "+"}${formatMoneyShort(Math.abs(gap), currency)}`}
          tone={gap < 0 ? "warning" : "success"}
        />
      </div>
      <div className="px-4 pb-2 pt-4">
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={months} margin={{ top: 4, right: 8, bottom: 0, left: 0 }} barCategoryGap="22%" barGap={2}>
            <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--chart-axis)" }} axisLine={false} tickLine={false} />
            <YAxis
              tickFormatter={(v) => formatMoney(Number(v), currency)}
              tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
              axisLine={false}
              tickLine={false}
              width={64}
            />
            <Tooltip
              formatter={(v) => formatMoneyFull(Number(v), currency)}
              cursor={{ fill: "var(--muted)" }}
              contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "var(--border)" }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" iconSize={10} />
            <Bar dataKey="plan" name="Plan" fill="var(--chart-muted)" maxBarSize={18} radius={[3, 3, 0, 0]} isAnimationActive={false} />
            <Bar dataKey="actual" name="Actual" stackId="real" fill="var(--chart-1)" maxBarSize={18} isAnimationActive={false} />
            <Bar dataKey="estimate" name="Forecast" stackId="real" fill={ESTIMATE_FILL} maxBarSize={18} radius={[3, 3, 0, 0]} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Card>
  )
}
